import { useMemo } from 'react';

import {
  A11Y_SELECTOR_ATTRIBUTE,
  type A11Y_SELECTORS,
} from './useA11ySelector.constants';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns React props object that can be used to pass to a component's `View` container
 * and/or a key/value pair that can be manually passed to a `View` container's `dataSet` property.
 *
 * @example
 * const App = () => {
 *  const a11ySelector = useA11ySelector('navbar.left-container');
 *
 *  return (
 *    <View {...a11ySelector.props} />
 *  );
 * }
 *
 * @example
 * const App = () => {
 *  const a11ySelector = useA11ySelector('navbar.left-container');
 *
 *  return (
 *    <View dataSet={{ otherDataAttribute: 'true', [a11ySelector.key]: a11ySelector.value }} />
 *  );
 * }
 */
export const useA11ySelector = (selector: Selector) => {
  return useMemo(() => {
    return {
      props: { dataSet: { [A11Y_SELECTOR_ATTRIBUTE]: selector } },
    };
  }, [selector]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Selector = ExtractStringValues<typeof A11Y_SELECTORS>;

type ExtractStringValues<T> = T extends string
  ? T
  : T extends Record<string, any>
    ? { [K in keyof T]: ExtractStringValues<T[K]> }[keyof T]
    : never;
